import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  createPermissionProfile,
  getPermissionProfile,
  updatePermissionProfile,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const GROUPS = [
  {
    title: 'Venda',
    description: 'Operações realizadas durante e depois da venda',
    fields: [
      ['cancelaItem', 'Cancelar item'],
      ['cancelaVenda', 'Cancelar venda'],
      ['consultaVendas', 'Consultar vendas'],
      ['reimprimeVenda', 'Reimprimir comprovante'],
      ['efetuaDesconto', 'Conceder desconto'],
      ['alteraPreco', 'Alterar preço na venda'],
      ['vendaEmEspera', 'Colocar venda em espera'],
      ['preVenda', 'Pré-vendas e cartões/comandas'],
      ['orcamento', 'Pedidos de venda (orçamento/pedido)'],
      ['alteraPrecoOrcamento', 'Alterar preço em pedido de venda'],
    ],
  },
  {
    title: 'Caixa',
    description: 'Sessão, conferência, sangria e suprimento',
    fields: [
      ['consultaResumo', 'Consultar resumo do caixa'],
      ['fechaCaixa', 'Conferir e encerrar caixa'],
      ['movimentaCaixa', 'Fazer sangria e suprimento'],
      ['editaMovimentoCaixa', 'Editar movimento de caixa'],
      ['alteraFormaPagamento', 'Alterar forma de pagamento da venda'],
    ],
  },
  {
    title: 'Cadastros',
    description: 'Manutenção de dados operacionais',
    fields: [
      ['cadastraProduto', 'Cadastrar e editar produtos'],
      ['cadastraCliente', 'Cadastrar e editar clientes'],
      ['cadastraUsuario', 'Cadastrar usuários'],
      ['gerenciaUsuarios', 'Editar, ativar e inativar usuários'],
      ['alteraPermissao', 'Gerenciar grupos de permissões'],
    ],
  },
  {
    title: 'Gestão',
    description: 'Módulos administrativos e movimentações',
    fields: [
      ['acessoGerencial', 'Acessar módulo gerencial'],
      ['acessoTodasFiliais', 'Acesso a todas as filiais'],
      ['movimentaEstoque', 'Movimentar estoque'],
      ['inventarioEstoque', 'Inventário / contagem de estoque'],
      ['transferenciaConsultar', 'Consultar transferências entre filiais'],
      ['transferenciaCriar', 'Criar transferências'],
      ['transferenciaEditar', 'Editar transferências (status Criado)'],
      ['transferenciaSeparar', 'Separar / baixa na origem'],
      ['transferenciaConcluir', 'Concluir / entrada no destino'],
      ['transferenciaCancelar', 'Cancelar transferências'],
      ['estoqueRelatorio', 'Relatórios de situação de estoque'],
      ['pedidoCompra', 'Pedidos de compra'],
      ['reajusteEstoque', 'Reajuste rápido de saldo na ficha'],
      ['movimentaConvenio', 'Movimentar convênio'],
      ['excluiRecebimentoConvenio', 'Excluir recebimento de convênio'],
      ['relatorios', 'Acessar relatórios'],
      ['alteraParametros', 'Alterar parâmetros e configurações'],
    ],
  },
];

const emptyProfile = () => ({
  descricao: '',
  percentualDesconto: 0,
  ...Object.fromEntries(GROUPS.flatMap((g) => g.fields).map(([key]) => [key, false])),
  // Alinha ao default do modelo/API: pré-venda/orçamento liberados no perfil.
  preVenda: true,
  orcamento: true,
  pedidoCompra: true,
});

export default function PermissionProfileForm({
  isEditMode = false,
  profileId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const params = useParams();
  const id = profileId ?? params.id;
  const editing = isEditMode || Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyProfile);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) return;
    const load = async () => {
      try {
        const response = await getPermissionProfile(id);
        setForm({ ...emptyProfile(), ...response.data });
      } catch (e) {
        setError(getApiErrorMessage(e, 'Não foi possível carregar o grupo.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [editing, id]);

  const toggleGroup = (group, checked) => {
    setForm((prev) => ({
      ...prev,
      ...Object.fromEntries(group.fields.map(([key]) => [key, checked])),
    }));
  };

  const allFields = GROUPS.flatMap((group) => group.fields.map(([key]) => key));
  const allChecked = allFields.every((key) => form[key]);
  const someChecked = allFields.some((key) => form[key]);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        percentualDesconto: Number(String(form.percentualDesconto || 0).replace(',', '.')),
      };
      if (editing) await updatePermissionProfile(id, payload);
      else await createPermissionProfile(payload);
      if (onSaved) {
        onSaved(editing ? 'Grupo de permissões atualizado' : 'Grupo de permissões criado');
      } else {
        navigate('/permission-profiles');
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível salvar o grupo.'));
    } finally {
      setSaving(false);
    }
  };

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/permission-profiles');
  };

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1100, mx: 'auto' }}>
      {!embedded && (
        <>
          <Typography variant="h4" fontWeight={900}>
            {editing ? 'Editar grupo de permissões' : 'Novo grupo de permissões'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            As permissões são aplicadas a todos os usuários vinculados ao grupo
          </Typography>
        </>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={save}>
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    label="Nome do grupo"
                    value={form.descricao}
                    onChange={(e) => setForm((p) => ({ ...p, descricao: e.target.value }))}
                    placeholder="Ex.: Operador, Fiscal de caixa, Gerente"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Desconto máximo (%)"
                    value={form.percentualDesconto ?? 0}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, percentualDesconto: e.target.value }))
                    }
                    inputMode="decimal"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            ...Object.fromEntries(
                              allFields.map((key) => [key, e.target.checked])
                            ),
                          }))
                        }
                      />
                    }
                    label="Todas"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            {GROUPS.map((group) => {
              const selected = group.fields.filter(([key]) => form[key]).length;
              const groupChecked = selected === group.fields.length;
              return (
                <Grid item xs={12} md={6} key={group.title}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="h6" fontWeight={800}>{group.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.description}
                          </Typography>
                        </Box>
                        <Checkbox
                          checked={groupChecked}
                          indeterminate={selected > 0 && !groupChecked}
                          onChange={(e) => toggleGroup(group, e.target.checked)}
                          inputProps={{ 'aria-label': `Selecionar grupo ${group.title}` }}
                        />
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Stack>
                        {group.fields.map(([key, label]) => (
                          <FormControlLabel
                            key={key}
                            control={
                              <Switch
                                checked={Boolean(form[key])}
                                onChange={(e) =>
                                  setForm((p) => ({ ...p, [key]: e.target.checked }))
                                }
                              />
                            }
                            label={label}
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={fechar}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar grupo'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
